import {gql} from '@apollo/client';
import React from 'react';

import {OBJECTS_REGISTERABLE_PERMISSION} from 'auth/PermissionConstants';
import {getRegistrySupportedOligoAwareTypes, TagSchema} from 'constants/RegistryConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import checkSoftInvariant from 'lib/checkSoftInvariant';
import {
  TagSchemaCreatableEntityTypeFragment,
  tagSchemaToCreatableEntityType,
} from 'registry/create-bioentity/entity-creation-config/CreatableEntityTypeGetter';
import {isEntityCreationFeatureEnabled} from 'registry/create-bioentity/entity-creation-config/EntityCreationConfig';
import {EntityCreationFeatureType} from 'registry/create-bioentity/entity-creation-config/EntityCreationConfigTypes';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';

import {RegistrationTableTagSchemasQuery} from './__generated/RegistrationTableTagSchemasQuery';

export const REGISTRATION_TABLE_TAG_SCHEMAS_QUERY = gql`
  query RegistrationTableTagSchemasQuery @skipAudit @engComponent(name: REGISTRY) {
    tagSchemas {
      ...TagSchemaCreatableEntityTypeFragment
    }
  }
  ${TagSchemaCreatableEntityTypeFragment.fragment}
`;

export enum RegistrationTableKind {
  REGISTRATION_TABLE = 'REGISTRATION_TABLE',
  LEGACY_REGISTRATION_TABLE = 'LEGACY_REGISTRATION_TABLE',
}

const TABLE_KIND_TO_FEATURE_TYPE: Record<RegistrationTableKind, EntityCreationFeatureType> = {
  [RegistrationTableKind.REGISTRATION_TABLE]: EntityCreationFeatureType.REGISTRATION_TABLE,
  [RegistrationTableKind.LEGACY_REGISTRATION_TABLE]: EntityCreationFeatureType.LEGACY_REGISTRATION_TABLE,
};

export interface RegistrationTableTagSchemaSelectorProps {
  onChange: (schema: TagSchema) => void;
  selectedTagSchemaId: string | null;
  tableKind: RegistrationTableKind;
}

/**
 * A component that wraps TagSchemaSelector with feature filtering logic for registration tables.
 * Filters out schemas based on entity creation features enabled for each schema type.
 */
export function RegistrationTableTagSchemaSelector({
  onChange,
  selectedTagSchemaId,
  tableKind,
}: RegistrationTableTagSchemaSelectorProps): JSX.Element {
  // Map table type to feature type
  const featureType = TABLE_KIND_TO_FEATURE_TYPE[tableKind];

  return (
    <BenchlingQuery<RegistrationTableTagSchemasQuery>
      query={REGISTRATION_TABLE_TAG_SCHEMAS_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
    >
      {(data) => {
        // Create a mapping from schema ID to entity type for filtering
        const schemaIdToEntityType = Object.fromEntries(
          data.tagSchemas.map((schemaData) => [schemaData.id, tagSchemaToCreatableEntityType(schemaData)])
        );

        const tagSchemaFilterFn = (schema: TagSchema): boolean => {
          const creatableEntityType = schemaIdToEntityType[schema.id];

          // TODO(BNCH-129436): Upgrade to assert
          checkSoftInvariant(creatableEntityType != null, {
            message: `Expected schema.id ${schema.id} to be present in schemaIdToEntityType mapping`,
            expirationDate: '2025-08-05',
            errorInDevAndTest: true,
            sendToSentry: true,
          });
          if (creatableEntityType == null) {
            // Show the schema if we don't know what entity type it is
            return true;
          }

          return isEntityCreationFeatureEnabled(creatableEntityType, featureType);
        };

        return (
          <TagSchemaSelector
            onChange={onChange}
            selectedTagSchemaId={selectedTagSchemaId}
            tagSchemaFilterFn={tagSchemaFilterFn}
            requiredPermissions={[OBJECTS_REGISTERABLE_PERMISSION]}
            oligoAwareFolderItemTypes={getRegistrySupportedOligoAwareTypes()}
          />
        );
      }}
    </BenchlingQuery>
  );
}
